/**
 * LA PANTALLA PRINCIPAL — NIVEL 1.
 *
 * Lo primero que ve el admin al entrar: **cómo va toda la operación**, y **la
 * lista de todos los negocios** con sus cifras. Clic en uno → se entra en él.
 *
 * Es lo que el propietario pidió: *"un dashboard general... y si hacemos clic en
 * alguna opción podríamos ver todos los negocios y entrar en cada uno de ellos"*.
 * La consola de conversaciones deja de ser la portada y baja a ser una
 * herramienta de depuración.
 */
import { useQuery } from '@tanstack/react-query'

import { Contadores, type ContadoresVista } from '../components/Contadores'
import { SaludInfra } from '../components/SaludInfra'
import { Tendencia } from '../components/Tendencia'
import { api } from '../lib/api'
import { cifrasPortada, piePortada } from '../lib/portada-cifras'
import { PERIODOS_PORTADA, diasDePeriodo, usePeriodoPortada } from '../lib/periodo-portada'
import { useIncluirTestsPortada } from '../lib/use-incluir-tests'
import { FallosLaboratorio } from './fallos-laboratorio'
import { pct, TablaNegocios, type FilaNegocio } from './tabla-negocios'

type Negocio = FilaNegocio

interface Overview {
    periodo: { desde: string; hasta: string }
    totales: ContadoresVista
    conversion: FilaNegocio['conversion']
    negocios: Negocio[]
    /** FR-040 — demos excluidos de los agregados; listados aparte. */
    demos?: { negocios: number; items?: Negocio[] }
    /** FR-036/037 — altas incompletas excluidas del cómputo. */
    incompletos?: { negocios: number; porEstado: Record<string, number> }
    /** Lo excluido por ser del laboratorio de personas (FR-055). */
    laboratorio: { conversaciones: number; incluidos: boolean }
    sinAtribuir: {
        eventos: number
        conversaciones: number
        anterioresAlPanel: number
        observandoDesde: string | null
    }
}

export function DashboardAdmin() {
    const [periodo, setPeriodo] = usePeriodoPortada()
    /**
     * spec 021 — se recuerda, como el periodo. Sin valor guardado nace
     * encendido: en las demos el laboratorio ES el tráfico. La banda ámbar
     * de abajo sigue avisando cuando está dentro.
     */
    const [incluirTests, setIncluirTests] = useIncluirTestsPortada()

    const { data, isLoading, error } = useQuery<Overview>({
        queryKey: ['admin-overview', periodo, incluirTests],
        queryFn: () => api.get<Overview>(`/overview?periodo=${periodo}&incluirTests=${incluirTests ? '1' : '0'}`),
    })

    if (isLoading) return <p className="text-sm text-neutral-500">Cargando…</p>
    if (error || !data) return <p className="text-sm text-rose-400">No se pudo cargar la operación.</p>

    const cifras = cifrasPortada(data)

    return (
        <div className="space-y-6">
            <header className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-neutral-100">Toda la operación</h2>
                    <p className="text-xs text-neutral-500">{piePortada(cifras.nProduccion, cifras.nDemos)}</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    {/*
                      EL INTERRUPTOR DE LOS TESTS.

                      Cuando está encendido, las cifras de esta pantalla INCLUYEN
                      nuestro laboratorio. Por eso no es un `checkbox` discreto:
                      es un botón que cambia de color y que, encendido, viene
                      acompañado de una banda ámbar imposible de no ver. Esta
                      pantalla no puede acabar en una captura delante de un
                      cliente sin que se note que no son sus números.
                    */}
                    <button
                        onClick={() => setIncluirTests(!incluirTests)}
                        aria-pressed={incluirTests}
                        className={
                            'min-h-11 rounded-lg border px-5 py-2.5 text-sm font-medium transition md:min-h-0 ' +
                            (incluirTests
                                ? 'border-amber-500/60 bg-amber-500/15 text-amber-300'
                                : 'border-neutral-800 text-neutral-500 hover:text-neutral-300')
                        }
                    >
                        🧪 {incluirTests ? 'Tests INCLUIDOS' : 'Incluir tests'}
                    </button>

                <div className="flex rounded-md border border-neutral-800">
                    {PERIODOS_PORTADA.map((p) => (
                        <button
                            key={p.clave}
                            onClick={() => setPeriodo(p.clave)}
                            className={
                                'min-h-11 px-3 py-1 text-xs transition md:min-h-0 ' +
                                (periodo === p.clave
                                    ? 'bg-neutral-800 text-neutral-100'
                                    : 'text-neutral-500 hover:text-neutral-300')
                            }
                        >
                            {p.etiqueta}
                        </button>
                    ))}
                </div>
                </div>
            </header>

            {/*
              LA BANDA. Solo aparece con los tests dentro, y ocupa el ancho
              entero: el que mire esta pantalla no puede confundirla con las
              cifras reales del cliente ni en una captura de pantalla.
            */}
            {data.laboratorio.incluidos && (
                <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-2 text-xs text-amber-300">
                    🧪 <strong>Estás viendo las cifras CON el tráfico de pruebas incluido.</strong> Estos NO son los
                    números del cliente: incluyen {data.laboratorio.conversaciones.toLocaleString('es-ES')}{' '}
                    conversaciones del laboratorio de personas.
                </div>
            )}

            {cifras.soloDemos && (
                <p className="text-xs text-amber-400/80" data-testid="portada-solo-demos">
                    No hay negocios en producción. Las cifras de abajo son de las{' '}
                    <strong>{cifras.nDemos}</strong> demos.
                </p>
            )}

            {((data.incompletos?.negocios ?? 0) > 0 || (cifras.nDemos > 0 && !cifras.soloDemos)) && (
                <p className="text-xs text-neutral-500" data-testid="incompletos-fuera">
                    Fuera del cómputo
                    {(data.incompletos?.negocios ?? 0) > 0
                        ? `: ${data.incompletos!.negocios} negocio(s) con alta incompleta${
                              Object.keys(data.incompletos!.porEstado).length > 0
                                  ? ` (${Object.entries(data.incompletos!.porEstado)
                                        .map(([e, n]) => `${e}: ${n}`)
                                        .join(', ')})`
                                  : ''
                          }`
                        : ''}
                    {cifras.nDemos > 0 && !cifras.soloDemos
                        ? `${(data.incompletos?.negocios ?? 0) > 0 ? ' ·' : ':'} ${cifras.nDemos} demo(s)`
                        : ''}
                    .
                </p>
            )}

            {/*
              LA SALUD VA ARRIBA, Y ES DELIBERADO.
              Cuando todo está bien ocupa una tira de cuatro líneas y no estorba.
              Cuando algo se rompe, crece — y es lo primero que se ve al entrar,
              antes que ninguna cifra de negocio. Si hay fuego, el fuego primero.
            */}
            <SaludInfra />

            <Contadores
                datos={cifras.totales}
                accountId={0}
                periodo={periodo}
                incluirTests={incluirTests}
                porNegocio={cifras.negociosDelTotal}
            />

            <div className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-4">
                <div className="flex items-baseline gap-3">
                    <span className="text-2xl font-semibold tabular-nums text-emerald-400">
                        {pct(cifras.conversion)}
                    </span>
                    <span className="text-sm text-neutral-300">de conversión global</span>
                </div>
                <p className="mt-1 text-xs text-neutral-500">
                    De quienes vinieron a reservar, cuántas acabaron en cita:{' '}
                    {cifras.conversion.numerador} de {cifras.conversion.denominador}. Las abiertas y las que
                    solo consultaron no entran.
                </p>
            </div>

            <Tendencia dias={diasDePeriodo(periodo)} incluirTests={incluirTests} />

            {/* ── LA LISTA DE NEGOCIOS ── el puente del nivel 1 al nivel 2 ───────── */}
            {data.negocios.length > 0 && (
                <section className="space-y-2">
                    <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Negocios</h3>
                    <TablaNegocios negocios={data.negocios} />
                </section>
            )}

            {/*
              FR-040 — demos visibles y entrables, fuera del cómputo global.
              Misma tabla que producción para poder verificar la integración
              (nivel 2 / agenda / consola) sin mezclar cifras.
            */}
            {(data.demos?.items?.length ?? 0) > 0 && (
                <section className="space-y-2" data-testid="demos-lista">
                    <h3 className="text-xs font-medium uppercase tracking-wide text-amber-500/80">
                        Demos
                        <span className="ml-2 font-normal normal-case tracking-normal text-neutral-500">
                            no cuentan en los agregados
                        </span>
                    </h3>
                    <TablaNegocios negocios={data.demos!.items!} tono="demo" demo />
                </section>
            )}

            <FallosLaboratorio />

            {/*
              FR-016 — LO QUE NO HEMOS PODIDO ATRIBUIR. NUNCA ESCONDIDO.

              Si se ocultara, el panel enseñaría cifras que SUBCUENTAN en silencio.
              Pero hay que partirlo en dos, o la honestidad se vuelve un susto:

                · Lo ANTERIOR al panel (~24.600) nunca fue atribuible. No es que nos
                  lo perdiéramos: ocurrió antes de que existiéramos. Meterlo en el
                  mismo saco daría un "25.297 sin atribuir" que suena a panel roto —
                  y a fuerza de dar sustos, el aviso se acaba ignorando.
                · Lo que ocurrió MIENTRAS observábamos y aun así no supimos de quién
                  era: ESE es el número que importa, y es el que se enseña.

              Solo lo ve el admin. No es asunto del cliente, pero sí nuestro.
            */}
            <div className="space-y-1 border-t border-neutral-900 pt-3 text-[11px] text-neutral-600">
                {/*
                  LO EXCLUIDO, DICHO — no descontado en silencio.

                  El 2026-07-18 estas cifras pasaron de 5.414 conversaciones a 2:
                  el 99,9% era nuestro propio laboratorio de personas, contado
                  como tráfico real de la clínica. Una caída así sin explicación
                  parece un panel roto (o, peor, se acepta sin mirar). Con el
                  recuento delante es una corrección que se puede auditar.
                */}
                {data.laboratorio.conversaciones > 0 && !data.laboratorio.incluidos && (
                    <p>
                        🧪 Excluidas <strong>{data.laboratorio.conversaciones.toLocaleString('es-ES')}</strong>{' '}
                        conversaciones del <strong>laboratorio de personas</strong> (tests nuestros contra la misma
                        cuenta de Chatwoot que el negocio real). No son tráfico del cliente.
                    </p>
                )}
                {(data.sinAtribuir.eventos > 0 || data.sinAtribuir.conversaciones > 0) && (
                    <p className="text-amber-600/80">
                        ⚠️ Sin atribuir <strong>mientras observábamos</strong>: {data.sinAtribuir.eventos} eventos ·{' '}
                        {data.sinAtribuir.conversaciones} conversaciones. No están contados arriba.
                    </p>
                )}

                {data.sinAtribuir.anterioresAlPanel > 0 && data.sinAtribuir.observandoDesde && (
                    <p>
                        ⓘ Otros {data.sinAtribuir.anterioresAlPanel.toLocaleString('es-ES')} eventos son{' '}
                        <strong>anteriores</strong> a que el panel empezara a observar (
                        {new Date(data.sinAtribuir.observandoDesde).toLocaleDateString('es-ES')}). Nunca fueron
                        atribuibles: no faltan, ocurrieron antes.
                    </p>
                )}
            </div>
        </div>
    )
}
